import * as React from "react"
import { Eye, EyeOff } from "lucide-react"
import { cn } from "../../../lib/utils"
import { Input } from "./input"

interface PasswordInputProps extends Omit<React.ComponentProps<"input">, "type"> {
  showPasswordLabel?: string
  hidePasswordLabel?: string
}

function PasswordInput({
  showPasswordLabel = "Show password",
  hidePasswordLabel = "Hide password",
  value,
  onKeyDown,
  className,
  disabled,
  ...props
}: PasswordInputProps) {
  const [showPassword, setShowPassword] = React.useState(false)
  const [hasUserTyped, setHasUserTyped] = React.useState(false)

  React.useEffect(() => {
    if (value === "") {
      setHasUserTyped(false)
      setShowPassword(false)
    }
  }, [value])

  function handleKeyDown(e: React.KeyboardEvent<HTMLInputElement>) {
    // Only unlock when the field is empty at keydown time — i.e. the user is
    // typing from scratch. If the field already has a value (autofill or
    // pre-fill), a single keystroke must not expose the existing content.
    // Autofill never fires keydown; navigation keys have key.length > 1.
    if (
      e.key.length === 1 &&
      !e.ctrlKey && !e.metaKey && !e.altKey &&
      (value === "" || value === undefined)
    ) {
      setHasUserTyped(true)
    }
    onKeyDown?.(e)
  }

  return (
    <div className="relative">
      <Input
        {...props}
        type={showPassword ? "text" : "password"}
        value={value}
        onKeyDown={handleKeyDown}
        disabled={disabled}
        // pr-10 (physical, not logical pe-10) so tailwind-merge dedupes it
        // against the base Input's pr-3 — otherwise both padding props emit and
        // the smaller right padding can win, letting long values run under the
        // 40px (w-10) toggle button. The toggle is positioned right-0 (also
        // physical) to stay on the same side as this padding in every direction.
        className={cn("pr-10", className)}
      />
      {hasUserTyped && (
        <button
          type="button"
          onClick={() => setShowPassword((v) => !v)}
          disabled={disabled}
          aria-label={showPassword ? hidePasswordLabel : showPasswordLabel}
          aria-pressed={showPassword}
          className="absolute right-0 top-0 flex h-full w-10 items-center justify-center text-[var(--muted-foreground)] hover:text-[var(--foreground)] disabled:opacity-50"
        >
          {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
        </button>
      )}
    </div>
  )
}

export { PasswordInput }
